import Button from 'tailwind-components/Button/Button';
import FileUpload from 'components/FileUpload/FileUpload';
import Modal from 'tailwind-components/Modal/Modal';
import ScrollableTabs from 'tailwind-components/ScrollableTabs/ScrollableTabs';
import Tab from '@mui/material/Tab';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ArrowRight, CircleX, Download, Info, UploadCloud } from 'lucide-react';
import { FormControl, MenuItem, TextField } from '@mui/material';
import ResultsList from './ResultsList';
import {
  catOf,
  downloadErrorLog,
  downloadOnboardingTemplate,
  uploadBulkOnboarding
} from './bulkOnboardingApi';

const TABS = {
  DOWNLOAD: 'download',
  UPLOAD: 'upload'
};

const MAX_UPLOAD_BYTES = 2 * 1024 * 1024; // 2MB

const UPLOAD_ACCEPT = {
  'text/csv': ['.csv']
};

/**
 * two-tab bulk onboarding modal (Download Template / Upload File) plus the
 * running/results phases. ONEILL-886 — O'Neill only.
 * @param open - whether the modal is visible
 * @param onClose - called when the modal should close
 */
export default function BulkOnboardingModal({ open, onClose }) {
  const agencyData = useSelector(state => state.agencyManagement.agencyData);

  const agencyOptions = useMemo(() => {
    const info = agencyData?.agencyInformation;
    if (!info) return [];
    const downline = info.Downline_Agencies_Three || [];
    return [info, ...downline]
      .map(a => ({ name: a?.name?._value, value: a?.id?._value }))
      .filter(o => o.value);
  }, [agencyData]);

  const ownAgencyId = agencyData?.agencyInformation?.id?._value ?? '';

  const [activeTab, setActiveTab] = useState(TABS.DOWNLOAD);
  const [isDownloading, setIsDownloading] = useState(false);
  const [hasDownloaded, setHasDownloaded] = useState(false);
  const [downloadError, setDownloadError] = useState('');

  const [targetAgencyId, setTargetAgencyId] = useState(ownAgencyId);
  const [files, setFiles] = useState([]);
  const [phase, setPhase] = useState('form'); // 'form' | 'running' | 'results'
  const [results, setResults] = useState([]);
  const [fileError, setFileError] = useState(null);
  const [uploadError, setUploadError] = useState('');

  // keep the default target agency in sync once agency data resolves.
  useEffect(() => {
    if (ownAgencyId && !targetAgencyId) setTargetAgencyId(ownAgencyId);
  }, [ownAgencyId, targetAgencyId]);

  const resetAndClose = () => {
    setActiveTab(TABS.DOWNLOAD);
    setHasDownloaded(false);
    setDownloadError('');
    setFiles([]);
    setPhase('form');
    setResults([]);
    setFileError(null);
    setUploadError('');
    onClose();
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    setDownloadError('');
    try {
      await downloadOnboardingTemplate();
      setHasDownloaded(true);
    } catch (e) {
      setDownloadError(e?.message || 'Could not download the template.');
    } finally {
      setIsDownloading(false);
    }
  };

  const canSubmit = !!targetAgencyId && files.length > 0;

  const handleUpload = async () => {
    if (!canSubmit) return;
    setPhase('running');
    setUploadError('');
    setFileError(null);
    try {
      const data = await uploadBulkOnboarding({
        targetAgencyId,
        file: files[0]
      });
      if (data?.fileError) {
        setFileError(data.fileError);
        setResults([]);
      } else {
        setResults(Array.isArray(data?.results) ? data.results : []);
        setFileError(null);
      }
      setPhase('results');
    } catch (e) {
      setUploadError(e?.message || 'The upload failed. Please try again.');
      setPhase('form');
    }
  };

  // ---- running phase ----
  if (open && phase === 'running') {
    return (
      <Modal
        active={open}
        closeModal={() => {}}
        title='Processing invites...'
        size='large'
        isLoading
        hideFooter
      />
    );
  }

  // ---- results phase ----
  if (open && phase === 'results') {
    return (
      <Modal
        active={open}
        closeModal={resetAndClose}
        title='Bulk invite results'
        size='large'
        showScrollbar
        footer={
          <div className='flex w-full flex-col gap-2'>
            {!fileError && results.some(r => catOf(r) === 'error') && (
              <div className='flex items-start gap-2 rounded-md border border-primary-light bg-primary-lighter px-3 py-2 text-sm'>
                <Info
                  className='mt-0.5 size-4 shrink-0 text-primary-darkened'
                  aria-hidden
                />
                <p className='m-0'>
                  Fix the rows directly in this file and re-upload it — no
                  need to remove the{' '}
                  <span className='font-semibold'>Error_Message</span> column.
                </p>
              </div>
            )}
            <div className='flex w-full flex-wrap items-center justify-end gap-2'>
              {!fileError && results.some(r => catOf(r) === 'error') && (
                <Button
                  variant='secondary'
                  size='small'
                  text='Download error_log.csv'
                  icon={<Download size={16} />}
                  onClick={() => downloadErrorLog(results)}
                />
              )}
              <Button
                variant='primary'
                size='small'
                text='Done'
                onClick={resetAndClose}
              />
            </div>
          </div>
        }
      >
        {fileError ? (
          <div className='flex items-start gap-2 rounded-md border border-semantic-danger/40 bg-semantic-danger/10 px-3 py-2 text-sm text-semantic-danger'>
            <CircleX className='mt-0.5 size-4 shrink-0' aria-hidden />
            <div>
              <p className='m-0 font-semibold'>The file was rejected</p>
              <p className='m-0 mt-0.5 whitespace-pre-line'>{fileError}</p>
            </div>
          </div>
        ) : (
          <ResultsList results={results} />
        )}
      </Modal>
    );
  }

  // ---- form phase (tabs) ----
  const footer =
    activeTab === TABS.UPLOAD ? (
      <div className='flex w-full flex-wrap justify-end gap-2'>
        <Button
          variant='secondary'
          size='small'
          text='Cancel'
          onClick={resetAndClose}
        />
        <Button
          variant='primary'
          size='small'
          text='Send invites'
          icon={<UploadCloud size={18} />}
          isDisabled={!canSubmit}
          onClick={handleUpload}
        />
      </div>
    ) : (
      <div className='flex w-full justify-end'>
        <Button
          variant='secondary'
          size='small'
          text='Close'
          onClick={resetAndClose}
        />
      </div>
    );

  return (
    <Modal
      active={open}
      closeModal={resetAndClose}
      title='Bulk onboarding invites'
      size='large'
      showScrollbar
      footer={footer}
    >
      <div className='flex flex-col gap-4 text-left'>
        <ScrollableTabs
          value={activeTab}
          onChange={(_event, nextTab) => setActiveTab(nextTab)}
          aria-label='Bulk onboarding options'
        >
          <Tab value={TABS.DOWNLOAD} label='Download Template' />
          <Tab value={TABS.UPLOAD} label='Upload File' />
        </ScrollableTabs>

        {activeTab === TABS.DOWNLOAD && (
          <div className='flex flex-col gap-4'>
            <p className='m-0 text-sm text-grey'>
              Download the CSV template, fill in one agent per row
              (First&nbsp;Name, Last&nbsp;Name, NPN, Email), then upload it in
              the next tab. Do not change the column headers.
            </p>

            <div>
              <Button
                variant='primaryDarkened'
                size='medium'
                rounded='small'
                text='Download Template'
                icon={<Download size={18} />}
                isLoading={isDownloading}
                isDisabled={isDownloading}
                onClick={handleDownload}
              />
            </div>

            {downloadError && (
              <p className='m-0 text-sm text-semantic-danger'>
                {downloadError}
              </p>
            )}

            {hasDownloaded && (
              <div className='flex items-start gap-2 rounded-md border border-primary-light bg-primary-lighter px-3 py-2 text-sm'>
                <Info
                  className='mt-0.5 size-4 shrink-0 text-primary-darkened'
                  aria-hidden
                />
                <div>
                  <p className='m-0'>
                    Now upload your file. You can always come back to this
                    section if you want to upload another file.
                  </p>
                  <button
                    type='button'
                    onClick={() => setActiveTab(TABS.UPLOAD)}
                    className='mt-1 inline-flex items-center gap-1 text-sm font-semibold text-primary-darkened hover:underline'
                  >
                    Go to Upload File
                    <ArrowRight className='size-4' aria-hidden />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === TABS.UPLOAD && (
          <div className='flex flex-col gap-4'>
            <div className='flex items-start gap-2 rounded-md border border-primary-light bg-primary-lighter px-3 py-2 text-sm'>
              <Info
                className='mt-0.5 size-4 shrink-0 text-primary-darkened'
                aria-hidden
              />
              <div className='text-black'>
                <p className='m-0'>
                  Only <span className='font-semibold'>CSV</span> files. Maximum{' '}
                  <span className='font-semibold'>100</span> agents per upload —
                  use multiple uploads for more.
                </p>
                <p className='m-0 mt-0.5'>
                  All agents are onboarded as{' '}
                  <span className='font-semibold'>Downline Agents</span>.
                  Downline agency admins must be onboarded individually.
                </p>
              </div>
            </div>

            <FormControl fullWidth>
              <TextField
                select
                label='Destination agency'
                value={targetAgencyId}
                onChange={e => setTargetAgencyId(e.target.value)}
                size='small'
                helperText='All agents in the file will be invited to this agency.'
              >
                {agencyOptions.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.name}
                  </MenuItem>
                ))}
              </TextField>
            </FormControl>

            <FileUpload
              files={files}
              onFilesChange={(_i, newFiles) => setFiles(newFiles)}
              accept={UPLOAD_ACCEPT}
              required
              multiple={false}
              maxSize={MAX_UPLOAD_BYTES}
              title='Onboarding file (.csv)'
            />

            {uploadError && (
              <p className='m-0 text-sm text-semantic-danger'>{uploadError}</p>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

BulkOnboardingModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};
